import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ ignoreMobileResize: true });

export default class Top {
  constructor() {
    // TOPページはMVがcontainerの外にあるためpaddingTop不要
    // common.jsのheaderFuncが付与するpaddingTopを即座にリセット
    const container = document.querySelector('.container');
    if (container) {
      container.style.paddingTop = '0px';
    }

    this._pendingLeadReveal = null;
    // MVスライダーを初期化（均一フェード・自動再生）
    this.mvSwiper = this.mvSliderFunc();
    this.charRevealFunc();
    this.leadRevealFunc();
    this.worldGridRevealFunc();
    this.planViewerFunc();
    this.periodRevealFunc();

    // ScrollTriggerを使う処理は全てwindow load後に一括初期化する
    // 画像・フォント等の全リソースロード完了後にレイアウトが確定し、
    // pin-spacerの位置計算が正確になる
    this.initScrollTriggers();
  }

  initScrollTriggers() {
    const init = () => {
      // headerのpaddingTopをフォントロード後のヘッダー高さで再設定
      // (common.jsのheaderFuncはDOMContentLoaded時に実行されるため、
      //  フォントロード前の高さで計算されている可能性がある)
      const isSP = window.innerWidth <= 767;
      const header = isSP
        ? document.getElementById('spMenu')
        : document.getElementById('gHeader');
      // 1. pinなしのScrollTrigger（レイアウトに影響しない）
      // MV画像は下揃え・全体表示のため、スクロール連動パララックスは行わない
      this.worldItemParallax();
      this.heroPlotRevealFunc();
      // 2. pin付きScrollTriggerを上→下の順に生成
      this.designRevealFunc();
      // 3. 全ScrollTrigger生成後に位置を再計算
      ScrollTrigger.refresh();
    };

    // designセクション前のlazy画像がレイアウトを確定するまで待つ
    const waitForLazyImages = () => {
      const designTrigger = document.querySelector('.js-designReveal');
      if (!designTrigger) return Promise.resolve();

      // designセクションより前にある未完了のlazy画像を取得
      const allImages = [...document.querySelectorAll('img[loading="lazy"]')];
      const preceding = allImages.filter(
        (img) => designTrigger.compareDocumentPosition(img) & Node.DOCUMENT_POSITION_PRECEDING
      );
      const pending = preceding.filter((img) => !img.complete);
      if (!pending.length) return Promise.resolve();

      return Promise.all(
        pending.map(
          (img) => new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })
        )
      );
    };

    window.addEventListener('load', () => {
      document.fonts.ready.then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            waitForLazyImages().then(() => {
              requestAnimationFrame(() => init());
            });
          });
        });
      });
    });
  }

  // MVスライダー: フェード・自動再生。最終シーンのみタイトル表示、キャプションはスライド毎に切替
  mvSliderFunc() {
    const el = document.querySelector('.js-mvSlider');
    if (!el) return null;

    const slides = el.querySelectorAll('.swiper-slide');
    if (slides.length < 2) return null;

    const ttl = document.querySelector('.js-mvTtl');
    const caption = document.querySelector('.js-mvCaption');

    // フェード切替に重ねる各シーンのスロームズーム（Ken Burns風）
    const SPEED = 2000;
    const DELAY = 5000;
    const ZOOM_SCALE = 1.08;
    // 表示区間(DELAY)＋次シーンへのフェードアウト(SPEED)の間ずっと拡大し続ける
    const ZOOM_DURATION = (DELAY + SPEED) / 1000;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const syncScene = (swiper) => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (ttl) {
        const isLast = !!activeSlide && activeSlide.getAttribute('data-mv-last') === 'true';
        ttl.classList.toggle('is-active', isLast);
      }
      if (caption && activeSlide) {
        const cap = activeSlide.getAttribute('data-mv-caption');
        if (cap) caption.textContent = cap;
      }

      // アクティブになったシーンのみズーム開始。切替前のシーンは拡大したまま
      // フェードアウトするため、リセットによるカクつきは起きない
      if (reduceMotion) return;
      const img = activeSlide?.querySelector('.photo__image');
      if (!img) return;
      // 下揃えを保つため下端基点で拡大（ズーム中も建物の足元が動かない）
      gsap.fromTo(img,
        { scale: 1, transformOrigin: 'center bottom' },
        { scale: ZOOM_SCALE, duration: ZOOM_DURATION, ease: 'none', overwrite: 'auto' }
      );
    };

    return new Swiper(el, {
      modules: [EffectFade, Autoplay, Pagination],
      effect: 'fade',
      fadeEffect: { crossFade: true },
      loop: false,
      rewind: true,
      slidesPerView: 1,
      speed: SPEED,
      allowTouchMove: false,
      autoplay: { delay: DELAY, disableOnInteraction: false, waitForTransition: false },
      pagination: {
        el: '.js-mvPagination',
        type: 'bullets',
        clickable: true,
      },
      on: {
        init: syncScene,
        slideChange: syncScene,
      },
    });
  }

  leadRevealFunc() {
    const container = document.querySelector('.js-leadReveal');
    if (!container) return;

    const lines = container.querySelectorAll('.uspArea__leadLine');
    if (!lines.length) return;

    const BASE_DELAY = 400;
    let fired = false;

    const reveal = () => {
      if (fired) return;
      fired = true;
      lines.forEach((line, i) => {
        line.style.transitionDelay = `${i * BASE_DELAY}ms`;
        line.classList.add('is-visible');
      });
    };

    // charRevealFunc から連鎖発火される（呼び出し順に依存しない）
    this._pendingLeadReveal = reveal;

    // フォールバック: charReveal 連鎖が発火しなかった場合に備える
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => reveal(), 1000);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-100px 0px' }
    );

    observer.observe(container);
  }

  worldGridRevealFunc() {
    const grid = document.querySelector('.js-worldGridReveal');
    if (!grid) return;

    const items = grid.querySelectorAll('.conceptSect__worldItem');
    if (!items.length) return;

    const STAGGER_DELAY = 350;
    const CLIP_DURATION = 1400;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            items.forEach((item, i) => {
              const itemDelay = i * STAGGER_DELAY;
              item.style.transitionDelay = `${itemDelay}ms`;

              const city = item.querySelector('.conceptSect__worldCity');
              if (city) {
                city.style.transitionDelay = `${itemDelay + CLIP_DURATION}ms`;
              }

              item.classList.add('is-revealed');
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-100px 0px' }
    );

    observer.observe(grid);
  }

  // テキストを1文字ずつ<span>に分割する
  static splitChars(parent, fragment) {
    parent.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        [...node.textContent].forEach((char) => {
          const span = document.createElement('span');
          span.className = 'charReveal__char';
          span.textContent = char;
          fragment.appendChild(span);
        });
      } else if (node.tagName === 'BR') {
        fragment.appendChild(node.cloneNode(true));
      } else {
        // <span class="en">などの要素: ラッパーを保持しつつ中身を分割
        const wrapper = node.cloneNode(false);
        Top.splitChars(node, wrapper);
        fragment.appendChild(wrapper);
      }
    });
  }

  // charReveal を発火し、全文字アニメーションの総時間(ms)を返す
  // dryRun=true の場合は DOM を変更せず総時間のみ返す
  static triggerReveal(el, { dryRun = false } = {}) {
    const PAUSE_CHARS = /[、。,.\s]/;
    const BASE_DELAY = 70;
    const PAUSE_DELAY = 280;

    const chars = el.querySelectorAll('.charReveal__char');
    const customDelay = Number(el.dataset.charDelay);
    const baseD = customDelay || BASE_DELAY;
    const pauseD = customDelay ? customDelay * 4 : PAUSE_DELAY;
    const offset = Number(el.dataset.charOffset) || 0;
    let elapsed = offset;
    chars.forEach((char) => {
      if (!dryRun) {
        char.style.transitionDelay = `${elapsed}ms`;
        char.classList.add('is-visible');
      }
      elapsed += PAUSE_CHARS.test(char.textContent) ? pauseD : baseD;
    });
    return elapsed;
  }

  charRevealFunc() {
    const els = document.querySelectorAll('.js-charReveal');
    if (!els.length) return;

    els.forEach((el) => {
      const fragment = document.createDocumentFragment();
      Top.splitChars(el, fragment);
      el.textContent = '';
      el.appendChild(fragment);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const totalTime = Top.triggerReveal(entry.target);

            // data-char-chain: 同コンテナ内のmanualな兄弟要素を連鎖発火
            const chainSelector = entry.target.dataset.charChain;
            if (chainSelector) {
              const container = entry.target.parentElement;
              const chainEl = container?.querySelector(chainSelector);
              if (chainEl && 'charManual' in chainEl.dataset) {
                setTimeout(() => Top.triggerReveal(chainEl), totalTime * 0.8);
              }
            }

            // data-char-chain-lead: leadReveal を連鎖発火
            if ('charChainLead' in entry.target.dataset && this._pendingLeadReveal) {
              setTimeout(() => this._pendingLeadReveal(), totalTime * 0.8);
              this._pendingLeadReveal = null;
            }

            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-100px 0px' }
    );

    els.forEach((el) => {
      if (!('charManual' in el.dataset)) observer.observe(el);
    });
  }

  worldItemParallax() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const items = document.querySelectorAll('.conceptSect__worldItem');
    items.forEach((item) => {
      const photo = item.querySelector('.photo');
      if (!photo) return;

      gsap.fromTo(
        photo,
        { y: '0%' },
        {
          y: '-16.67%',
          ease: 'none',
          scrollTrigger: {
            trigger: item,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    });
  }

  heroPlotRevealFunc() {
    const trigger = document.querySelector('.js-heroPlotReveal');
    if (!trigger) return;

    const heroPic = trigger.querySelector(':scope > .photo .photo__pic');
    const plotSendai = trigger.querySelector('.conceptSect__heroPlot--sendai');
    const plotTeizenji = trigger.querySelector('.conceptSect__heroPlot--teizenji');
    if (!heroPic || !plotSendai || !plotTeizenji) return;

    const isSP = window.innerWidth <= 767;

    // 初期状態: 拡大+ブラー
    gsap.set(heroPic, {
      scale: 1.15,
      filter: `blur(${isSP ? '1px' : '2px'})`,
    });

    // ズームアウト（scrub連動）— セクションがビューポートに入ってから中央に来るまで
    gsap.fromTo(heroPic,
      { scale: 1.15, filter: `blur(${isSP ? '1px' : '2px'})` },
      {
        scale: 1,
        filter: 'blur(0px)',
        ease: 'power1.out',
        scrollTrigger: {
          trigger,
          start: 'top 80%',
          end: 'center center',
          scrub: 1,
        },
      }
    );

    // sendai: ズームアウトが70%進んだらフェードイン（以前の挙動）
    let sendaiRevealed = false;
    gsap.fromTo(heroPic,
      { '--_plotProgress': 0 },
      {
        '--_plotProgress': 1,
        scrollTrigger: {
          trigger,
          start: 'top 80%',
          end: 'center center',
          scrub: 1,
          onUpdate: (self) => {
            if (!sendaiRevealed && self.progress >= 0.7) {
              sendaiRevealed = true;
              gsap.to(plotSendai, { opacity: 1, duration: 1.2, ease: 'power2.out' });
            }
          },
        },
      }
    );

    // teizenji: セクション最下部がビューポートに入ったらフェードイン
    ScrollTrigger.create({
      trigger,
      start: 'bottom bottom',
      onEnter: () => {
        gsap.to(plotTeizenji, { opacity: 1, duration: 1.2, ease: 'power2.out' });
      },
    });
  }

  designRevealFunc() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const trigger = document.querySelector('.js-designReveal');
    const clip = document.querySelector('.js-designReveal__clip');
    const image = clip?.querySelector('.photo__image');
    const caption = clip?.querySelector('.photo__caption');
    if (!trigger || !clip || !image) return;

    // JSが動作する場合のみキャプションを隠す（フォールバック: CSSでは常時表示）
    clip.classList.add('is-ready');

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isFirefox = /firefox/i.test(navigator.userAgent);

    // レスポンシブ判定（初回ロード時に固定。リサイズ時の再計算は行わない）
    const isSP = window.innerWidth <= 767;
    const clipStart = isSP
      ? 'inset(25vh 15vw 35vh 15vw)'
      : 'inset(20vh 25vw 30vh 25vw)';
    const scaleStart = isSP ? 0.9 : 0.85;
    // Safari: scrub 1.5秒の平滑化遅延分、pin区間を延長してアニメーション完了前のpin解除を防止
    const baseScroll = isSP ? 640 : 800;
    // scrubの平滑化遅延分、pin区間を延長してアニメーション完了前のpin解除を防止
    const scrubBuffer = isSafari ? 160 : isFirefox ? 120 : 100;
    const totalScroll = `${baseScroll + scrubBuffer}vh`;
    const revealWeight = 0.3;
    const captionFade = 0.05;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger,
        start: 'top top',
        end: `+=${totalScroll}`,
        pin: true,
        pinSpacing: true,
        scrub: isSafari ? 1.5 : 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onEnter: () => { clip.style.willChange = 'clip-path, transform'; },
        onLeave: () => { clip.style.willChange = 'auto'; },
        onEnterBack: () => { clip.style.willChange = 'clip-path, transform'; },
        onLeaveBack: () => { clip.style.willChange = 'auto'; },
      },
    });

    // Phase 1: クリップパス展開 + ズームイン
    tl.fromTo(clip,
      { clipPath: clipStart, scale: scaleStart, force3D: true },
      { clipPath: 'inset(0vh 0vw 0vh 0vw)', scale: 1, force3D: true, ease: 'power1.inOut', duration: revealWeight },
      0
    );

    // 光が差し込むエフェクト
    // Safari: filterアニメーションはclip-pathとの同時進行で再ペイントが重すぎるため除去
    if (!isSafari) {
      // Phase 1: スクロール連動で brightness 上昇
      // ピーク到達後: スクロールに依存しない時間ベースでゆっくり復帰（カメラの露出補正のような自然な減衰）
      const brightnessPeak = 1.6;
      const brightnessDecaySec = 1.8;
      let decayTween = null;
      let hasPeaked = false;

      tl.fromTo(image,
        { filter: 'brightness(1)' },
        { filter: `brightness(${brightnessPeak})`, ease: 'power2.in', duration: revealWeight },
        0
      );

      tl.eventCallback('onUpdate', () => {
        const progress = tl.progress();
        if (!hasPeaked && progress >= revealWeight) {
          hasPeaked = true;
          decayTween = gsap.to(image, {
            filter: 'brightness(1)',
            duration: brightnessDecaySec,
            ease: 'power2.out',
          });
        }
        if (hasPeaked && progress < revealWeight) {
          hasPeaked = false;
          if (decayTween) decayTween.kill();
        }
      });
    }

    // キャプションフェードイン（Phase 1終了直後）
    if (caption) {
      tl.to(caption, { opacity: 1, duration: captionFade, ease: 'none' }, revealWeight);
    }

    // Phase 2: 画像パン（上→下）
    // object-fit:coverのまま、object-positionを0%→100%に補間して最下部まで到達
    tl.fromTo(image,
      { objectPosition: '50% 0%' },
      { objectPosition: '50% 100%', ease: 'none', duration: 1 - revealWeight },
      revealWeight
    );
  }

  periodRevealFunc() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const trigger = document.querySelector('.js-periodReveal');
    const visual = document.querySelector('.js-periodReveal__visual');
    const catchEl = document.querySelector('.js-periodReveal__catch');
    if (!trigger || !visual) return;

    const isSP = window.innerWidth <= 767;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // Safari: brightness + blur + clipPath + scale 同時はWebKitで重すぎるため、filterを除去
    gsap.set(visual, {
      scale: 1.3,
      ...(!isSafari && { filter: `brightness(1.2) blur(${isSP ? '8px' : '14px'})` }),
      opacity: 0,
      clipPath: 'inset(0% 100% 0% 0%)',
      transformOrigin: 'left center',
      force3D: true,
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visual.style.willChange = 'transform, opacity';

            // ライトリーク用オーバーレイ（Safari除外）
            let leak = null;
            if (!isSafari) {
              leak = document.createElement('div');
              Object.assign(leak.style, {
                position: 'absolute',
                inset: '0',
                background: 'radial-gradient(ellipse at 60% 40%, rgba(255,240,210,.6), transparent 70%)',
                opacity: '0',
                pointerEvents: 'none',
                mixBlendMode: 'screen',
                zIndex: '1',
              });
              visual.style.position = 'relative';
              visual.appendChild(leak);
            }

            // クリップマスク展開 + opacity（2.2秒で収束）
            gsap.to(visual, {
              clipPath: 'inset(0% 0% 0% 0%)',
              ...(!isSafari && { filter: 'brightness(1) blur(0px)' }),
              opacity: 1,
              duration: 2.2,
              ease: 'power3.out',
            });

            // scale（5.5秒でゆったり収束）
            const scaleDuration = 5500;
            gsap.to(visual, {
              scale: 1,
              duration: scaleDuration / 1000,
              ease: 'power1.out',
              onComplete: () => { visual.style.willChange = 'auto'; },
            });

            // テキスト発火: クリップマスク展開から少し遅れて開始
            if (catchEl) {
              setTimeout(() => Top.triggerReveal(catchEl), 800);
            }

            // ライトリーク: 序盤で瞬間的にフラッシュして消える（Safari除外）
            if (leak) {
              gsap.fromTo(leak,
                { opacity: 0 },
                {
                  opacity: 1,
                  duration: 0.4,
                  ease: 'power2.in',
                  onComplete: () => {
                    gsap.to(leak, {
                      opacity: 0,
                      duration: 1.2,
                      ease: 'power2.out',
                      onComplete: () => { leak.remove(); },
                    });
                  },
                }
              );
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-5% 0px' }
    );

    observer.observe(trigger);
  }

  planViewerFunc() {
    const $viewer = document.querySelector('.js-planViewer');
    if (!$viewer) return;

    const tabs = $viewer.querySelectorAll('.planSect__navItem');
    const slideCount = $viewer.querySelectorAll('.swiper-slide').length;
    if (slideCount < 2) return;

    function syncTabs(index) {
      tabs.forEach((tab, i) => {
        tab.classList.toggle('is-active', i === index);
      });
    }

    const swiper = new Swiper($viewer.querySelector('.planSect__slider'), {
      modules: [Navigation],
      loop: true,
      slidesPerView: 1,
      speed: 600,
      navigation: {
        prevEl: $viewer.querySelector('.planSect__prev'),
        nextEl: $viewer.querySelector('.planSect__next')
      },
      on: {
        slideChange: function () {
          syncTabs(this.realIndex);
        }
      }
    });

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const index = Number(tab.dataset.planIndex);
        swiper.slideToLoop(index);
      });
    });
  }
}
